import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card, Modal } from '../components/ui';
import { Plus, Pencil, Trash2, FolderKanban, ArrowRight, BarChart2 } from 'lucide-react';

const tomtProsjekt = () => ({
  navn: '', kode: '', beskrivelse: '', kunde_id: '', budsjett: '',
  status: 'aktiv', start_dato: '', slutt_dato: '', farge: '#3b82f6',
});

const Prosjekter = () => {
  const [prosjekter, setProsjekter] = useState(null);
  const [kontakter, setKontakter] = useState([]);
  const [visAvsluttet, setVisAvsluttet] = useState(false);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [skjema, setSkjema] = useState(tomtProsjekt());
  const [rapport, setRapport] = useState(null);

  const last = useCallback(() => {
    api.get('/prosjekt', { params: visAvsluttet ? { alle: 1 } : {} })
       .then((r) => setProsjekter(r.data));
  }, [visAvsluttet]);

  useEffect(() => { last(); }, [last]);
  useEffect(() => {
    api.get('/kontakter?type=kunde').then((r) => setKontakter(r.data)).catch(() => {});
  }, []);

  const aapneNy = () => { setRedigerer(null); setSkjema(tomtProsjekt()); setOpen(true); };
  const aapneRediger = (p) => {
    setRedigerer(p);
    setSkjema({
      navn: p.navn, kode: p.kode || '', beskrivelse: p.beskrivelse || '',
      kunde_id: p.kunde_id || '', budsjett: p.budsjett || '',
      status: p.status || 'aktiv', start_dato: p.start_dato || '',
      slutt_dato: p.slutt_dato || '', farge: p.farge || '#3b82f6',
    });
    setOpen(true);
  };

  const lagre = async () => {
    const payload = {
      ...skjema,
      kunde_id: skjema.kunde_id || null,
      budsjett: skjema.budsjett === '' ? 0 : parseFloat(skjema.budsjett),
    };
    try {
      if (redigerer) await api.put(`/prosjekt/${redigerer.id}`, payload);
      else await api.post('/prosjekt', payload);
      setOpen(false); last();
    } catch (e) {
      alert(e.response?.data?.error || 'Klarte ikke lagre.');
    }
  };

  const slett = async (p) => {
    if (!window.confirm(`Slette prosjektet «${p.navn}»? Bilag og fakturaer beholdes, men mister koblingen.`)) return;
    await api.delete(`/prosjekt/${p.id}`); last();
    if (rapport?.prosjekt?.id === p.id) setRapport(null);
  };

  const visRapport = async (p) => {
    const r = await api.get(`/prosjekt/${p.id}/rapport`);
    setRapport(r.data);
  };

  if (!prosjekter) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <PageHeader
        title="Prosjekter"
        subtitle="Tag bilag og fakturaer med prosjekt for lønnsomhet pr kunde/oppdrag"
        actions={<button className="btn-primary" onClick={aapneNy}><Plus size={14} /> Nytt prosjekt</button>}
      />

      <Card style={{ marginBottom: 12, padding: 10 }}>
        <label className="flex items-center" style={{ gap: 6, fontSize: 12, cursor: 'pointer', margin: 0 }}>
          <input type="checkbox" checked={visAvsluttet}
                 onChange={(e) => setVisAvsluttet(e.target.checked)} />
          Vis avsluttede prosjekter
        </label>
      </Card>

      <div className="grid" style={{
        gridTemplateColumns: rapport ? '1fr 1fr' : '1fr', gap: 12,
      }}>
        <Card style={{ padding: 0 }}>
          {prosjekter.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <FolderKanban size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
              <p className="fm-text-muted" style={{ margin: 0, fontSize: 13 }}>
                Ingen prosjekter enda. Opprett ditt første prosjekt.
              </p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 6 }}></th>
                  <th>Kode</th>
                  <th>Navn</th>
                  <th>Kunde</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Budsjett</th>
                  <th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {prosjekter.map((p) => (
                  <tr key={p.id}>
                    <td style={{ padding: 0, background: p.farge, width: 4 }}></td>
                    <td><span className="fm-text-muted" style={{ fontSize: 11 }}>{p.kode || '—'}</span></td>
                    <td style={{ fontWeight: 600 }}>{p.navn}</td>
                    <td>{p.kunde_navn || <span className="fm-text-muted">—</span>}</td>
                    <td>
                      <span className={`badge ${p.status === 'aktiv' ? 'badge-success' : 'badge-neutral'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>{p.budsjett ? kr(p.budsjett) : '—'}</td>
                    <td>
                      <div className="flex" style={{ gap: 4 }}>
                        <button className="btn-ghost" onClick={() => visRapport(p)} title="Vis rapport">
                          <BarChart2 size={12} />
                        </button>
                        <button className="btn-ghost" onClick={() => aapneRediger(p)} title="Rediger">
                          <Pencil size={12} />
                        </button>
                        <button className="btn-ghost" onClick={() => slett(p)} title="Slett"
                                style={{ color: 'var(--theme-danger)' }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {rapport && (
          <Card>
            <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                              background: rapport.prosjekt.farge, marginRight: 8 }} />
                {rapport.prosjekt.navn}
              </h3>
              <button className="btn-ghost" onClick={() => setRapport(null)} style={{ fontSize: 11 }}>Lukk</button>
            </div>

            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <Mini label="Inntekter" verdi={kr(rapport.inntekt_total)} farge="var(--theme-success)" />
              <Mini label="Utgifter" verdi={kr(rapport.utgift_bilag)} farge="var(--theme-danger)" />
              <Mini label="Resultat" verdi={kr(rapport.resultat)}
                    farge={rapport.resultat >= 0 ? 'var(--theme-success)' : 'var(--theme-danger)'} />
              <Mini label="Bilag / Fakturaer" verdi={`${rapport.antall_bilag} / ${rapport.antall_fakturaer}`} />
            </div>

            {rapport.budsjett > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div className="flex items-center justify-between" style={{ fontSize: 11, marginBottom: 4 }}>
                  <span className="fm-text-muted">
                    Budsjettforbruk: {kr(rapport.utgift_bilag)} av {kr(rapport.budsjett)}
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {rapport.budsjett_brukt_pct?.toFixed(0)}%
                  </span>
                </div>
                <div style={{ height: 8, background: 'var(--theme-bg-hover)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(100, rapport.budsjett_brukt_pct || 0)}%`,
                    background: rapport.budsjett_brukt_pct > 100 ? 'var(--theme-danger)' : 'var(--theme-accent)',
                  }} />
                </div>
              </div>
            )}

            {rapport.fakturaer.length > 0 && (
              <details open style={{ marginBottom: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Fakturaer ({rapport.fakturaer.length})
                </summary>
                <table>
                  <tbody>
                    {rapport.fakturaer.map((f) => (
                      <tr key={f.id}>
                        <td style={{ fontSize: 11 }}>{f.fakturanummer}</td>
                        <td style={{ fontSize: 11 }}>{f.kunde_navn}</td>
                        <td style={{ fontSize: 11, textAlign: 'right' }}>{kr(f.sum_eks_mva)}</td>
                        <td style={{ width: 24 }}>
                          <Link to={`/faktura/${f.id}`}><ArrowRight size={12} /></Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {rapport.bilag.length > 0 && (
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Bilag ({rapport.bilag.length})
                </summary>
                <table>
                  <tbody>
                    {rapport.bilag.slice(0, 20).map((b) => (
                      <tr key={b.id}>
                        <td style={{ fontSize: 11 }}>{dato(b.dato)}</td>
                        <td style={{ fontSize: 11 }}>
                          <span className={`badge ${b.type === 'inntekt' ? 'badge-success' : 'badge-danger'}`}
                                style={{ fontSize: 9 }}>
                            {b.type}
                          </span>
                        </td>
                        <td style={{ fontSize: 11 }}>{b.beskrivelse}</td>
                        <td style={{ fontSize: 11, textAlign: 'right' }}>{kr(b.belop_eks_mva)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </Card>
        )}
      </div>

      <Modal
        open={open} onClose={() => setOpen(false)}
        title={redigerer ? 'Rediger prosjekt' : 'Nytt prosjekt'}
        footer={<>
          <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
          <button className="btn-primary" onClick={lagre}>Lagre</button>
        </>}
      >
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Navn *</label>
            <input value={skjema.navn} onChange={(e) => setSkjema({...skjema, navn: e.target.value})}
                   placeholder="F.eks. Hjemmeside-utvikling for ABC AS" />
          </div>
          <div>
            <label>Kode</label>
            <input value={skjema.kode} onChange={(e) => setSkjema({...skjema, kode: e.target.value})}
                   placeholder="PRO-2026-01" />
          </div>
          <div>
            <label>Status</label>
            <select value={skjema.status} onChange={(e) => setSkjema({...skjema, status: e.target.value})}>
              <option value="aktiv">Aktiv</option>
              <option value="avsluttet">Avsluttet</option>
            </select>
          </div>
          <div>
            <label>Kunde</label>
            <select value={skjema.kunde_id || ''}
                    onChange={(e) => setSkjema({...skjema, kunde_id: e.target.value})}>
              <option value="">— ingen —</option>
              {kontakter.map((k) => (
                <option key={k.id} value={k.id}>{k.navn}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Budsjett (eks. MVA)</label>
            <input type="number" step="0.01" value={skjema.budsjett}
                   onChange={(e) => setSkjema({...skjema, budsjett: e.target.value})}
                   placeholder="0 = ingen budsjett" />
          </div>
          <div>
            <label>Startdato</label>
            <input type="date" value={skjema.start_dato}
                   onChange={(e) => setSkjema({...skjema, start_dato: e.target.value})} />
          </div>
          <div>
            <label>Sluttdato</label>
            <input type="date" value={skjema.slutt_dato}
                   onChange={(e) => setSkjema({...skjema, slutt_dato: e.target.value})} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Farge</label>
            <div className="flex items-center" style={{ gap: 8 }}>
              <input type="color" value={skjema.farge}
                     onChange={(e) => setSkjema({...skjema, farge: e.target.value})}
                     style={{ width: 50, height: 32, padding: 2 }} />
              <span className="fm-text-muted" style={{ fontSize: 11 }}>
                Brukes for visuell merking i lister
              </span>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Beskrivelse</label>
            <textarea value={skjema.beskrivelse}
                      onChange={(e) => setSkjema({...skjema, beskrivelse: e.target.value})}
                      rows={3} />
          </div>
        </div>
      </Modal>
    </>
  );
};

const Mini = ({ label, verdi, farge }) => (
  <div className="card" style={{ padding: 10 }}>
    <div className="fm-text-muted" style={{ fontSize: 10, textTransform: 'uppercase',
                                            letterSpacing: '0.05em', fontWeight: 600 }}>
      {label}
    </div>
    <div style={{ fontSize: 16, fontWeight: 700, color: farge || 'var(--theme-text-primary)', marginTop: 2 }}>
      {verdi}
    </div>
  </div>
);

export default Prosjekter;
